import { html, type TemplateResult } from 'lit-html'

import type { App } from './app.ts'

/**
 * When the tide turns (#139, Q30.1).
 *
 * The server says the instant -- `turnsAt`, unix seconds, computed from the
 * island's day and never from a request's clock (Q17.1) -- and this module
 * only says it in the viewer's own words: the time in their zone and locale,
 * through `Intl.DateTimeFormat`, and how long is left, counted from the
 * browser's clock. That count is the one place the client's clock is used,
 * for display only and never for a rule: nothing turns on it, and a browser
 * whose clock is wrong is told a wrong number and nothing else.
 *
 * Both strings go through the content loaders and the ICU formatter as every
 * other sentence does; the time is one of the arguments.
 */

const MINUTE = 60_000

/** The turn, as an hour and a minute in the viewer's zone and locale. */
export function tideTime(app: App, turnsAt: number): string {
  return new Intl.DateTimeFormat(app.locale, { hour: 'numeric', minute: '2-digit' }).format(
    new Date(turnsAt * 1000),
  )
}

/**
 * What is left until the turn, as whole hours and minutes, never negative: a
 * tab left open across midnight reads nought until the day store refills.
 */
export function tideLeft(turnsAt: number, now: number): { hours: number; minutes: number } {
  const left = Math.max(0, Math.floor((turnsAt * 1000 - now) / MINUTE))
  return { hours: Math.floor(left / 60), minutes: left % 60 }
}

/**
 * The line: when, then how long. `key` names the sentence the time goes into
 * -- `tideTurnsAt` on the day, `tideGoesUnderAt` on the island going under --
 * and the countdown follows it either way.
 */
export function tideLine(
  app: App,
  key: 'tideTurnsAt' | 'tideGoesUnderAt',
  turnsAt: number | null,
  now: number = Date.now(),
): TemplateResult | null {
  if (turnsAt === null) return null
  return html`<p class="tide">
    <span class="tide-at">${app.t(key, { time: tideTime(app, turnsAt) })}</span>
    <span class="tide-in">${app.t('tideTurnsIn', tideLeft(turnsAt, now))}</span>
  </p>`
}

/**
 * Ask for a redraw once a minute, so the count under the strip moves without
 * anybody touching anything. Nothing else in the app has a clock (Q17.1),
 * and this one only ever asks for a render.
 */
export function tickEveryMinute(refresh: () => void): void {
  window.setInterval(refresh, MINUTE)
}
